import { Component, Inject, OnDestroy, OnInit, ViewEncapsulation } from '@angular/core';
import {
  UntypedFormBuilder,
  UntypedFormGroup,
  Validators,
} from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { MatSnackBar } from '@angular/material/snack-bar';
import { AdminService, MfaEnrollmentDto } from '@admin-api/index';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';

@Component({
  selector: 'user-list-mfa-enrollments',
  templateUrl: './mfa-enrollments.component.html',
  styleUrls: ['./mfa-enrollments.component.scss'],
  encapsulation: ViewEncapsulation.None,
})
export class UserListMfaEnrollmentsComponent implements OnInit, OnDestroy {
  isLoading = true;
  errorMessage: string = null;
  enrollments: MfaEnrollmentDto[] = [];
  optingInId: string = null;
  showResetForm = false;
  isResetting = false;
  resetMfaEnrollmentForm: UntypedFormGroup;
  private _destroy$ = new Subject<void>();

  constructor(
    public matDialogRef: MatDialogRef<UserListMfaEnrollmentsComponent>,
    @Inject(MAT_DIALOG_DATA) public data: IMfaEnrollmentsDialogData,
    private _adminService: AdminService,
    private _snackBar: MatSnackBar,
    private _formBuilder: UntypedFormBuilder,
  ) {}

  ngOnInit(): void {
    this.resetMfaEnrollmentForm = this._formBuilder.group({
      verification_details: ['', [Validators.required]],
    });
    this.fetchEnrollments();
  }

  ngOnDestroy(): void {
    this._destroy$.next();
    this._destroy$.complete();
  }

  fetchEnrollments(): void {
    this.isLoading = true;
    this.errorMessage = null;
    this._adminService
      .getUserMfaEnrollments({ id: this.data.user_id })
      .pipe(takeUntil(this._destroy$))
      .subscribe({
        next: (enrollments) => {
          this.enrollments = enrollments;
          this.isLoading = false;
        },
        error: (error) => {
          this.isLoading = false;
          this.errorMessage =
            error?.error?.message || error?.message || 'Unknown error';
        },
      });
  }

  optInSms(enrollment: MfaEnrollmentDto): void {
    this.optingInId = enrollment.id;
    this._adminService
      .optInUserSms({
        id: this.data.user_id,
        smsOptInParams: { enrollment_id: enrollment.id },
      })
      .pipe(takeUntil(this._destroy$))
      .subscribe({
        next: () => {
          this._snackBar.open('SMS opt-in successful.', null, {
            duration: 3000,
          });
          this.optingInId = null;
          this.fetchEnrollments();
        },
        error: (error) => {
          this.optingInId = null;
          this._snackBar.open(error.error?.message, null, {
            duration: 3000,
          });
        },
      });
  }

  showReset(): void {
    this.showResetForm = true;
  }

  cancelReset(): void {
    this.showResetForm = false;
    this.resetMfaEnrollmentForm.reset();
  }

  resetMfa(): void {
    if (this.resetMfaEnrollmentForm.invalid) {
      return;
    }
    this.isResetting = true;
    this._adminService
      .resetUserMfaEnrollments({
        id: this.data.user_id,
        userResetMfaEnrollmentsParams: {
          verification_details:
            this.resetMfaEnrollmentForm.value['verification_details'],
        },
      })
      .pipe(takeUntil(this._destroy$))
      .subscribe({
        next: () => {
          this._snackBar.open(
            'MFA enrollments have been reset successfully.',
            null,
            { duration: 3000 },
          );
          this.isResetting = false;
          this.showResetForm = false;
          this.resetMfaEnrollmentForm.reset();
          this.fetchEnrollments();
        },
        error: (error) => {
          this.isResetting = false;
          this._snackBar.open(error.error?.message, null, {
            duration: 3000,
          });
        },
      });
  }

  close(): void {
    this.matDialogRef.close();
  }

  typeIcon(type: string): string {
    switch (type) {
      case 'phone':
        return 'mat_outline:phone';
      case 'totp':
        return 'mat_outline:pin';
      case 'email':
        return 'mat_outline:email';
      default:
        return 'mat_outline:security';
    }
  }

  typeLabel(type: string): string {
    switch (type) {
      case 'phone':
        return 'SMS / Voice';
      case 'totp':
        return 'Authenticator App';
      case 'email':
        return 'Email';
      default:
        return type;
    }
  }
}

export interface IMfaEnrollmentsDialogData {
  user_name?: string;
  user_email?: string;
  user_id: string;
}
